import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="mb-4 text-2xl font-semibold text-slate-900 dark:text-white">{title}</h2>
    <div className="space-y-4 text-slate-600 dark:text-zinc-400">{children}</div>
  </section>
);

const TermsOfService: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - OmniScribe</title>
        <meta name="description" content="OmniScribe Terms of Service" />
      </Helmet>

      <div className="min-h-screen bg-white py-16 transition-colors duration-300 dark:bg-zinc-950">
        <div className="container mx-auto max-w-4xl px-4">
          <h1 className="h-hero mb-4">Terms of Service</h1>
          <p className="mb-12 text-slate-500 dark:text-zinc-400">Last updated: May 9, 2026</p>

          <div className="prose prose-slate max-w-none dark:prose-invert">
            <Section title="Introduction">
              <p>
                These Terms of Service govern your access to and use of OmniScribe, including our website,
                transcription tools, upload workflows, sharing features, APIs, billing features, and related
                services. When we say "Company", "we", "our", or "us", we mean NanoUltra, LLC, the operator
                of the OmniScribe service. When we say "OmniScribe", we mean the product and services provided
                by NanoUltra, LLC. When we say "you", we mean the person or organization using the service.
              </p>
              <p>
                By accessing or using OmniScribe, you agree to these Terms. If you do not agree, do not use
                the service. We may update these Terms from time to time. When we make material changes, we
                will update the date above and take reasonable steps to notify users where appropriate.
              </p>
            </Section>

            <Section title="Account Terms">
              <ol className="list-decimal space-y-3 pl-6">
                <li>You are responsible for maintaining the security of your account and credentials.</li>
                <li>You must provide accurate account information and keep it up to date.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>You may not use OmniScribe for unlawful, harmful, abusive, or infringing purposes.</li>
                <li>You may not create accounts through automated means or attempt to bypass usage limits.</li>
              </ol>
            </Section>

            <Section title="Use of the Service">
              <p>
                OmniScribe provides audio and video transcription, content analysis, summaries, exports,
                collaboration, sharing, and related workflow features. You are responsible for ensuring that
                you have the necessary rights, permissions, and consents to upload, transcribe, process, and
                share any audio, video, text, or other content you submit.
              </p>
              <p>
                You agree not to interfere with the service, reverse engineer protected parts of the service,
                overload our systems, scrape data in violation of these Terms, or use OmniScribe to process
                content that violates applicable law or the rights of others.
              </p>
            </Section>

            <Section title="Payment, Subscriptions, and Plan Changes">
              <ol className="list-decimal space-y-3 pl-6">
                <li>Paid plans are billed in advance according to the plan and billing interval you select.</li>
                <li>Payments are processed by Stripe or another payment processor. We do not store full card details.</li>
                <li>When you upgrade from a free plan to a paid plan, the new plan may take effect immediately.</li>
                <li>Downgrades and user-initiated cancellations generally take effect at the end of the current paid period.</li>
                <li>Fees are exclusive of taxes unless otherwise stated. You are responsible for applicable taxes where required.</li>
                <li>
                  Refund requests are handled according to our{' '}
                  <Link
                    to="/refund-policy"
                    className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    Refund Policy
                  </Link>
                  .
                </li>
              </ol>
            </Section>

            <Section title="Cancellation and Termination">
              <p>
                You can cancel a paid subscription from your account billing settings. Cancellation stops
                renewal and normally allows you to keep paid features until the end of the current paid term.
                Deleting your account may cancel access immediately.
              </p>
              <p>
                We may suspend or terminate access to OmniScribe if you violate these Terms, create risk for
                other users or our systems, fail to pay required fees, or use the service in a way that may
                create legal or security exposure. Termination may result in loss of access to your account
                and content.
              </p>
            </Section>

            <Section title="Content Ownership and Responsibility">
              <p>
                You retain ownership of content you upload to OmniScribe, including audio, video, transcripts,
                exports, and related materials. We claim no ownership over your uploaded content.
              </p>
              <p>
                You grant OmniScribe the limited rights needed to host, process, transcribe, analyze, store,
                display, export, and share your content as necessary to provide the service. We may remove or
                restrict content if we reasonably believe it violates these Terms, applicable law, or the rights
                of others.
              </p>
            </Section>

            <Section title="Uptime, Security, and Third-Party Services">
              <p>
                We take reliability and security seriously, but OmniScribe is provided on an "as is" and "as
                available" basis. We do not guarantee that the service will be uninterrupted, error-free, or
                meet every specific requirement.
              </p>
              <p>
                OmniScribe relies on third-party infrastructure and service providers for hosting, storage,
                networking, payments, authentication, email, analytics, and AI-assisted processing. These
                providers help us operate the service, but they may also experience downtime or service changes.
              </p>
            </Section>

            <Section title="Features and Changes">
              <p>
                We may add, change, limit, or remove features as the product evolves. We make no guarantee
                that any specific feature, integration, plan limit, file format, export format, or processing
                capability will remain available forever.
              </p>
            </Section>

            <Section title="Limitation of Liability">
              <p>
                To the maximum extent permitted by law, OmniScribe will not be liable for indirect, incidental,
                special, consequential, exemplary, or punitive damages, including loss of profits, data, goodwill,
                or business opportunities, arising from your use of or inability to use the service.
              </p>
              <p>
                Our total liability for any claim relating to the service will not exceed the amount you paid
                to OmniScribe for the service during the three months before the event giving rise to the claim,
                or USD $100 if you have not paid us.
              </p>
            </Section>

            <Section title="Contact">
              <p>If you have questions about these Terms, contact us at:</p>
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                <p className="font-medium text-slate-700 dark:text-zinc-300">NanoUltra, LLC</p>
                <p className="text-slate-600 dark:text-zinc-400">Delaware LLC</p>
                <p className="text-slate-600 dark:text-zinc-400">Founded January 13, 2026</p>
                <p className="text-slate-600 dark:text-zinc-400">Delaware File Number 10470896</p>
                <p className="text-slate-600 dark:text-zinc-400">OmniScribe</p>
                <p className="text-slate-600 dark:text-zinc-400">support@omniscribe.ai</p>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
